import axios from 'axios'
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { extractErrorMessage } from '../../utils/apitools'
import useAccount from '../useAccount'
import { keepWhileOnlyFilterChanges, skyProductURL, skyQueryKey } from './skyEndpoints'
import { labelGroupsToPie, type EntityMetricsGroup } from './labelGroupsToPie'
import type { PlatinumData } from '../../types/carbon/models/PlatinumData'
import type { LabelQuery } from '../../components/social-media/types'

interface EntityMetricsQueryResponse {
  groups: EntityMetricsGroup[]
  totalEntities: number
}

/**
 * `video_reach` is fixed rather than a parameter: this endpoint counts
 * DISTINCT ENTITIES contributing the metric (`EntityMetricsGroup.entityCount`),
 * not the metric's value, so any broadly-populated metric would answer the
 * same "how is output shared across this label's values" question —
 * `video_reach` is the one every post is expected to carry.
 */
const OUTPUT_SHARE_METRIC = 'video_reach'

export interface UseSkyOutputOpinionShareResult {
  /** A pie-ready breakdown of entity share by `labelKey`, or null while unloaded. */
  data: PlatinumData | null
  /** Filter-matching entities INCLUDING zero-metric ones. */
  totalEntities: number
  isLoading: boolean
  /** The previous filter's pie is on screen while the new one loads. */
  isUpdating: boolean
  error: Error | null
}

/**
 * The output/opinion split for one product, grouped by an entity label (e.g.
 * "opinion"): what share of this department's posts carries each value.
 *
 * `groupBy: {kind: 'label', labelKey}` — one bucket per distinct value of
 * `labelKey`, plus an `''` bucket for entities missing it (rendered
 * "Untagged" by `labelGroupsToPie`). Entity-level filters only, like
 * `useSkyEntityMetrics`: this endpoint counts entities, so a record-level
 * predicate has nothing to apply to.
 */
export function useSkyOutputOpinionShare(
  product: string,
  labelKey: string,
  enabled = true,
  labelQuery?: LabelQuery,
): UseSkyOutputOpinionShareResult {
  const account = useAccount()
  const labelsKey = useMemo(() => JSON.stringify(labelQuery ?? null), [labelQuery])

  const queryKey = skyQueryKey(product, 'output-share', { labelKey, labelsKey })
  const query = useQuery({
    queryKey,
    enabled: Boolean(product && labelKey && account.user && enabled),
    staleTime: 30000,
    retry: 1,
    retryDelay: 1000,
    // Kept across a filter change only: a new tag is a new question, and its
    // previous pie would be drawn under the new tag's name.
    placeholderData: keepWhileOnlyFilterChanges<EntityMetricsQueryResponse>(queryKey),
    queryFn: async ({ signal }): Promise<EntityMetricsQueryResponse> => {
      try {
        const res = await axios.post<EntityMetricsQueryResponse>(
          skyProductURL(product, '/entities/metrics/query'),
          {
            metrics: [{ metric: OUTPUT_SHARE_METRIC }],
            groupBy: { kind: 'label', labelKey },
            entity: labelQuery?.entity,
          },
          { signal },
        )
        return res.data
      } catch (err) {
        throw new Error(extractErrorMessage(err))
      }
    },
  })

  return {
    data: query.data ? labelGroupsToPie(query.data.groups) : null,
    // The slices' own total, not the response's totalEntities: a post with no
    // reach row sits in no slice, so the caption must count only what's drawn.
    totalEntities: query.data?.groups.reduce((sum, g) => sum + g.entityCount, 0) ?? 0,
    isLoading: query.isLoading,
    isUpdating: query.isPlaceholderData,
    error: query.error as Error | null,
  }
}

export default useSkyOutputOpinionShare
